import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { CategorySexFields, FormationDialogueFields, EtpField, EMPTY_FORMATION, EMPTY_DIALOGUE } from "../components/BilanComplementFields";
import { ArrowLeft, ArrowRight, Save, Send, CheckCircle, Upload, FileText, AlertCircle } from "lucide-react";
import ResponsabilityWarningDialog from "../components/ResponsabilityWarningDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Identification", description: "Entreprise et année" },
  { id: 2, title: "Effectifs", description: "Données sur le personnel" },
  { id: 3, title: "Emploi & Rotation", description: "Mouvements et taux de rotation" },
  { id: 4, title: "Absentéisme", description: "Congés et absences" },
  { id: 5, title: "Rémunération", description: "Masse salariale et charges" },
  { id: 6, title: "Formation, dialogue & sécurité", description: "Formation, dialogue social, accidents" },
  { id: 7, title: "Paiement & Soumission", description: "Preuve de paiement des 10%" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

export default function BilanFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEnterprise = searchParams.get("enterprise");
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [enterprises, setEnterprises] = useState([]);
  const [preuvePaiement, setPreuvePaiement] = useState(null);
  const [preuvePaiementFilename, setPreuvePaiementFilename] = useState(null);
  const [uploadingPreuve, setUploadingPreuve] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  // Pénalités
  const [penaliteInfo, setPenaliteInfo] = useState(null);
  const [preuvePenalite, setPreuvePenalite] = useState(null);
  const [preuvePenaliteFilename, setPreuvePenaliteFilename] = useState(null);
  const [uploadingPenalite, setUploadingPenalite] = useState(false);
  const [formData, setFormData] = useState({
    enterprise_id: preselectedEnterprise || "",
    annee: CURRENT_YEAR - 1,
    effectifs: {
      guinéens_hommes: 0,
      guinéens_femmes: 0,
      etrangers_hommes: 0,
      etrangers_femmes: 0,
      cdd: 0,
      cdi: 0,
      handicapes: 0,
      temporaires: 0,
      emplois_indirects: 0,
      emplois_indirects_details: "",
      // Tranches d'âge officielles IGT
      age_16_21: 0,
      age_22_30: 0,
      age_31_40: 0,
      age_41_50: 0,
      age_51_60: 0,
      age_61_65: 0,
      age_plus_65: 0,
    },
    emploi: {
      cadres: 0,
      agents_maitrise: 0,
      employes: 0,
      ouvriers: 0,
      embauches: 0,
      licenciements: 0,
      retraites: 0,
      demissions: 0,
      deces: 0,
      fin_contrat: 0,
      nombre_depart: 0,
      nombre_arrivee: 0,
      effectif_1er_janvier: 0,
      taux_rotation: 0,
      postes_nationalises: 0,
    },
    absenteisme: {
      jours_contractuels: 0,
      jours_conges: 0,
      jours_absence: 0,
      taux_absenteisme: 0,
    },
    remuneration: {
      masse_salariale: 0,
      heures_supplementaires: 0,
      primes_versees: 0,
      total_cnss: 0,
      total_vf: 0,
      total_cfpca: 0,
      total_ta: 0,
      total_rts: 0,
      total_cnss_patronale: 0,
      total_cnss_salariale: 0,
    },
    sante_securite: {
      accidents_travail: 0,
      maladies_professionnelles: 0,
      jours_arret: 0,
    },
    formation: EMPTY_FORMATION,
    dialogue_social: EMPTY_DIALOGUE,
    commentaires: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enterprisesRes] = await Promise.all([
          axios.get(`${API_URL}/enterprises`),
        ]);
        setEnterprises(enterprisesRes.data);

        if (id) {
          const bilanRes = await axios.get(`${API_URL}/bilans/${id}`);
          const bilan = bilanRes.data;
          setFormData({
            enterprise_id: bilan.enterprise_id,
            annee: bilan.annee,
            effectifs: bilan.effectifs || formData.effectifs,
            emploi: bilan.emploi || formData.emploi,
            absenteisme: bilan.absenteisme || formData.absenteisme,
            remuneration: bilan.remuneration || formData.remuneration,
            sante_securite: bilan.sante_securite || formData.sante_securite,
            formation: bilan.formation || EMPTY_FORMATION,
            dialogue_social: bilan.dialogue_social || EMPTY_DIALOGUE,
            commentaires: bilan.commentaires || "",
          });
          // Charger la preuve de paiement si elle existe
          if (bilan.preuve_paiement_10_filename) {
            setPreuvePaiementFilename(bilan.preuve_paiement_10_filename);
          }
          // Charger la preuve de pénalité si elle existe
          if (bilan.preuve_paiement_penalite_filename) {
            setPreuvePenaliteFilename(bilan.preuve_paiement_penalite_filename);
          }
          // Récupérer les infos de pénalité
          try {
            const penaliteRes = await axios.get(`${API_URL}/bilans/${id}/penalite`);
            setPenaliteInfo(penaliteRes.data);
          } catch (e) {
            console.error(/* PROD */ "Failed to fetch penalite info:", e);
          }
        }
      } catch (error) {
        console.error(/* PROD */ "Failed to fetch data:", error);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const updateField = (section, field, value) => {
    if (section) {
      setFormData((prev) => {
        const numValue = typeof value === "string" && !isNaN(value) ? parseFloat(value) || 0 : value;
        const newData = {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: numValue,
          },
        };
        
        // Calcul automatique du taux de rotation
        if (section === "emploi" && ["nombre_depart", "nombre_arrivee", "effectif_1er_janvier"].includes(field)) {
          const emploi = newData.emploi;
          const effectif = emploi.effectif_1er_janvier;
          if (effectif > 0) {
            const tauxRotation = ((emploi.nombre_depart + emploi.nombre_arrivee) / 2 / effectif) * 100;
            newData.emploi.taux_rotation = Math.round(tauxRotation * 10) / 10;
          }
        }
        
        // Calcul automatique du taux d'absentéisme
        if (section === "absenteisme" && ["jours_absence", "jours_contractuels"].includes(field)) {
          const absenteisme = newData.absenteisme;
          if (absenteisme.jours_contractuels > 0) {
            const tauxAbsenteisme = (absenteisme.jours_absence / absenteisme.jours_contractuels) * 100;
            newData.absenteisme.taux_absenteisme = Math.round(tauxAbsenteisme * 10) / 10;
          }
        }
        
        return newData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleUploadPreuve = async (file, bilanId) => {
    if (!file) return false;
    
    setUploadingPreuve(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      await axios.post(`${API_URL}/bilans/${bilanId}/upload-preuve-paiement`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setPreuvePaiementFilename(file.name);
      toast.success("Preuve de paiement uploadée avec succès");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload de la preuve");
      return false;
    } finally {
      setUploadingPreuve(false);
    }
  };

  const handleUploadPreuvePenalite = async (file, bilanId) => {
    if (!file) return false;
    
    setUploadingPenalite(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API_URL}/bilans/${bilanId}/upload-preuve-penalite`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setPreuvePenaliteFilename(file.name);
      toast.success("Preuve de paiement de pénalité uploadée avec succès");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload de la preuve de pénalité");
      return false;
    } finally {
      setUploadingPenalite(false);
    }
  };

  // Show warning dialog before submitting
  const handleSubmitClick = () => {
    setShowWarningDialog(true);
  };

  // Actually submit after confirmation
  const handleConfirmedSubmit = async () => {
    setShowWarningDialog(false);
    await handleSave(true);
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      let bilanId = id;
      
      if (id) {
        await axios.patch(`${API_URL}/bilans/${id}`, {
          effectifs: formData.effectifs,
          emploi: formData.emploi,
          absenteisme: formData.absenteisme,
          remuneration: formData.remuneration,
          sante_securite: formData.sante_securite,
          formation: formData.formation,
          dialogue_social: formData.dialogue_social,
          commentaires: formData.commentaires,
        });
      } else {
        const response = await axios.post(`${API_URL}/bilans`, formData);
        bilanId = response.data.id;
        
        if (!submit) {
          toast.success("Bilan créé en brouillon");
          navigate(`/bilans/${bilanId}/modifier`);
          return;
        }
      }
      
      // Si on soumet, uploader d'abord la preuve si elle existe
      if (submit) {
        if (preuvePaiement && !preuvePaiementFilename) {
          const uploadSuccess = await handleUploadPreuve(preuvePaiement, bilanId);
          if (!uploadSuccess) {
            setSaving(false);
            return;
          }
        }
        
        await axios.post(`${API_URL}/bilans/${bilanId}/submit`);
        toast.success("Bilan soumis avec succès");
        navigate("/bilans");
      } else {
        toast.success("Bilan enregistré");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="enterprise">Entreprise *</Label>
              <Select
                value={formData.enterprise_id}
                onValueChange={(value) => updateField(null, "enterprise_id", value)}
                disabled={!!id}
              >
                <SelectTrigger data-testid="select-enterprise">
                  <SelectValue placeholder="Sélectionner une entreprise" />
                </SelectTrigger>
                <SelectContent>
                  {enterprises.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.raison_sociale}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="annee">Année de déclaration *</Label>
              <Select
                value={formData.annee.toString()}
                onValueChange={(value) => updateField(null, "annee", parseInt(value))}
                disabled={!!id}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">Nationalité et Genre</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Guinéens - Hommes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.guinéens_hommes}
                    onChange={(e) => updateField("effectifs", "guinéens_hommes", e.target.value)}
                    data-testid="effectifs-guineens-hommes"
                  />
                </div>
                <div>
                  <Label>Guinéens - Femmes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.guinéens_femmes}
                    onChange={(e) => updateField("effectifs", "guinéens_femmes", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Étrangers - Hommes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.etrangers_hommes}
                    onChange={(e) => updateField("effectifs", "etrangers_hommes", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Étrangers - Femmes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.etrangers_femmes}
                    onChange={(e) => updateField("effectifs", "etrangers_femmes", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Types de Contrats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CDD</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.cdd}
                    onChange={(e) => updateField("effectifs", "cdd", e.target.value)}
                  />
                </div>
                <div>
                  <Label>CDI</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.cdi}
                    onChange={(e) => updateField("effectifs", "cdi", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Personnes Handicapées</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.handicapes}
                    onChange={(e) => updateField("effectifs", "handicapes", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Employés Temporaires</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.temporaires}
                    onChange={(e) => updateField("effectifs", "temporaires", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Emplois Indirects</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Emplois créés indirectement par l'entreprise (sous-traitants, prestataires, fournisseurs locaux, etc.)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre d'emplois indirects</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.emplois_indirects}
                    onChange={(e) => updateField("effectifs", "emplois_indirects", e.target.value)}
                    data-testid="effectifs-emplois-indirects"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Détails / Description des emplois indirects</Label>
                  <Textarea
                    placeholder="Ex: 50 employés chez le sous-traitant de nettoyage, 30 chauffeurs de transport..."
                    value={formData.effectifs.emplois_indirects_details || ""}
                    onChange={(e) => updateField("effectifs", "emplois_indirects_details", e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Tranches d'Âge (selon réglementation IGT)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>16 - 21 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_16_21}
                    onChange={(e) => updateField("effectifs", "age_16_21", e.target.value)}
                    data-testid="age-16-21"
                  />
                </div>
                <div>
                  <Label>22 - 30 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_22_30}
                    onChange={(e) => updateField("effectifs", "age_22_30", e.target.value)}
                    data-testid="age-22-30"
                  />
                </div>
                <div>
                  <Label>31 - 40 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_31_40}
                    onChange={(e) => updateField("effectifs", "age_31_40", e.target.value)}
                    data-testid="age-31-40"
                  />
                </div>
                <div>
                  <Label>41 - 50 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_41_50}
                    onChange={(e) => updateField("effectifs", "age_41_50", e.target.value)}
                    data-testid="age-41-50"
                  />
                </div>
                <div>
                  <Label>51 - 60 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_51_60}
                    onChange={(e) => updateField("effectifs", "age_51_60", e.target.value)}
                    data-testid="age-51-60"
                  />
                </div>
                <div>
                  <Label>61 - 65 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_61_65}
                    onChange={(e) => updateField("effectifs", "age_61_65", e.target.value)}
                    data-testid="age-61-65"
                  />
                </div>
                <div>
                  <Label>Plus de 65 ans</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.effectifs.age_plus_65}
                    onChange={(e) => updateField("effectifs", "age_plus_65", e.target.value)}
                    data-testid="age-plus-65"
                  />
                </div>
              </div>
            </div>
            <EtpField effectifs={formData.effectifs} onChange={updateField} />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">Catégories Professionnelles</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cadres</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.cadres}
                    onChange={(e) => updateField("emploi", "cadres", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Agents de Maîtrise</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.agents_maitrise}
                    onChange={(e) => updateField("emploi", "agents_maitrise", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Employés</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.employes}
                    onChange={(e) => updateField("emploi", "employes", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Ouvriers</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.ouvriers}
                    onChange={(e) => updateField("emploi", "ouvriers", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <CategorySexFields emploi={formData.emploi} onChange={updateField} />

            <div>
              <h3 className="font-semibold mb-4">Mouvements de Personnel</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Embauches</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.embauches}
                    onChange={(e) => updateField("emploi", "embauches", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Licenciements</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.licenciements}
                    onChange={(e) => updateField("emploi", "licenciements", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Retraites</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.retraites}
                    onChange={(e) => updateField("emploi", "retraites", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Démissions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.demissions}
                    onChange={(e) => updateField("emploi", "demissions", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Décès</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.deces}
                    onChange={(e) => updateField("emploi", "deces", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Fin de Contrat</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.fin_contrat}
                    onChange={(e) => updateField("emploi", "fin_contrat", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h3 className="font-semibold mb-4 text-primary">Calcul du Taux de Rotation</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Effectif au 1er janvier</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.effectif_1er_janvier}
                    onChange={(e) => updateField("emploi", "effectif_1er_janvier", e.target.value)}
                    data-testid="effectif-1er-janvier"
                  />
                </div>
                <div>
                  <Label>Nombre de départs</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.nombre_depart}
                    onChange={(e) => updateField("emploi", "nombre_depart", e.target.value)}
                    data-testid="nombre-depart"
                  />
                </div>
                <div>
                  <Label>Nombre d'arrivées</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.nombre_arrivee}
                    onChange={(e) => updateField("emploi", "nombre_arrivee", e.target.value)}
                    data-testid="nombre-arrivee"
                  />
                </div>
                <div>
                  <Label>Taux de Rotation (%)</Label>
                  <Input
                    type="number"
                    value={formData.emploi.taux_rotation}
                    readOnly
                    className="bg-slate-100 font-bold text-primary"
                    data-testid="taux-rotation"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Calculé automatiquement</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Autres Indicateurs</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Postes Nationalisés</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.emploi.postes_nationalises}
                    onChange={(e) => updateField("emploi", "postes_nationalises", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="font-semibold mb-4 text-amber-800">Données d'Absentéisme</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Jours contractuels</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.absenteisme.jours_contractuels}
                    onChange={(e) => updateField("absenteisme", "jours_contractuels", e.target.value)}
                    data-testid="jours-contractuels"
                  />
                </div>
                <div>
                  <Label>Jours de congés</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.absenteisme.jours_conges}
                    onChange={(e) => updateField("absenteisme", "jours_conges", e.target.value)}
                    data-testid="jours-conges"
                  />
                </div>
                <div>
                  <Label>Jours d'absence</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.absenteisme.jours_absence}
                    onChange={(e) => updateField("absenteisme", "jours_absence", e.target.value)}
                    data-testid="jours-absence"
                  />
                </div>
                <div>
                  <Label>Taux d'absentéisme (%)</Label>
                  <Input
                    type="number"
                    value={formData.absenteisme.taux_absenteisme}
                    readOnly
                    className="bg-slate-100 font-bold text-amber-700"
                    data-testid="taux-absenteisme"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Calculé automatiquement</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">Données de Rémunération</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Masse Salariale Annuelle (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.masse_salariale}
                    onChange={(e) => updateField("remuneration", "masse_salariale", e.target.value)}
                    data-testid="masse-salariale"
                  />
                </div>
                <div>
                  <Label>Heures Supplémentaires</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.heures_supplementaires}
                    onChange={(e) => updateField("remuneration", "heures_supplementaires", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Primes Versées (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.primes_versees}
                    onChange={(e) => updateField("remuneration", "primes_versees", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold mb-4 text-blue-800">Charges Sociales et Fiscales</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Total CNSS (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.total_cnss}
                    onChange={(e) => updateField("remuneration", "total_cnss", e.target.value)}
                    data-testid="total-cnss"
                  />
                </div>
                <div>
                  <Label>Total VF (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.total_vf}
                    onChange={(e) => updateField("remuneration", "total_vf", e.target.value)}
                    data-testid="total-vf"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Versement Forfaitaire</p>
                </div>
                <div>
                  <Label>Total CFPCA (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.total_cfpca}
                    onChange={(e) => updateField("remuneration", "total_cfpca", e.target.value)}
                    data-testid="total-cfpca"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Contrib. Formation Prof.</p>
                </div>
                <div>
                  <Label>Total TA (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.total_ta}
                    onChange={(e) => updateField("remuneration", "total_ta", e.target.value)}
                    data-testid="total-ta"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Taxe Apprentissage</p>
                </div>
                <div>
                  <Label>Total RTS (GNF)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.remuneration.total_rts}
                    onChange={(e) => updateField("remuneration", "total_rts", e.target.value)}
                    data-testid="total-rts"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Retenue à la Source</p>
                </div>
                <div>
                  <Label>CNSS part patronale (GNF, facultatif)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.remuneration.total_cnss_patronale || ""}
                    onChange={(e) => updateField("remuneration", "total_cnss_patronale", e.target.value)}
                    data-testid="total-cnss-patronale"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Si renseignée, classée en charges patronales</p>
                </div>
                <div>
                  <Label>CNSS part salariale (GNF, facultatif)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.remuneration.total_cnss_salariale || ""}
                    onChange={(e) => updateField("remuneration", "total_cnss_salariale", e.target.value)}
                    data-testid="total-cnss-salariale"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Classée en retenues salariales</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <FormationDialogueFields formData={formData} onChange={updateField} />
            <div>
              <h3 className="font-semibold mb-4">Santé et Sécurité au Travail</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Accidents de Travail</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.sante_securite.accidents_travail}
                    onChange={(e) => updateField("sante_securite", "accidents_travail", e.target.value)}
                    data-testid="accidents-travail"
                  />
                </div>
                <div>
                  <Label>Maladies Professionnelles</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.sante_securite.maladies_professionnelles}
                    onChange={(e) => updateField("sante_securite", "maladies_professionnelles", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Jours d'Arrêt</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.sante_securite.jours_arret}
                    onChange={(e) => updateField("sante_securite", "jours_arret", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Commentaires (Optionnel)</Label>
              <Textarea
                value={formData.commentaires}
                onChange={(e) => updateField(null, "commentaires", e.target.value)}
                placeholder="Informations complémentaires..."
                rows={4}
              />
            </div>
          </div>
        );

      case 7:
        const hasPenalite = penaliteInfo && penaliteInfo.type !== "aucune";
        const penaliteReady = !hasPenalite || preuvePenaliteFilename || preuvePenalite;
        const paiement10Ready = preuvePaiementFilename || preuvePaiement;
        
        return (
          <div className="space-y-6">
            {/* Section Pénalités (si applicable) */}
            {hasPenalite && (
              <div className={`border rounded-lg p-4 ${penaliteInfo.type === "retard" ? "bg-orange-50 border-orange-300" : "bg-red-50 border-red-300"}`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-6 h-6 mt-0.5 ${penaliteInfo.type === "retard" ? "text-orange-600" : "text-red-600"}`} />
                  <div className="flex-1">
                    <h3 className={`font-semibold ${penaliteInfo.type === "retard" ? "text-orange-800" : "text-red-800"}`}>
                      {penaliteInfo.type === "retard" ? "⚠️ Pénalité de Retard" : "🚨 Pénalité de Défaut de Déclaration"}
                    </h3>
                    <p className={`text-sm mt-1 ${penaliteInfo.type === "retard" ? "text-orange-700" : "text-red-700"}`}>
                      {penaliteInfo.message}
                    </p>
                    <div className={`mt-3 p-3 rounded-lg ${penaliteInfo.type === "retard" ? "bg-orange-100" : "bg-red-100"}`}>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Effectif:</span>
                          <span className="ml-2 font-semibold">{penaliteInfo.effectif} employés</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Taux:</span>
                          <span className="ml-2 font-semibold">{penaliteInfo.taux_par_employe?.toLocaleString()} GNF/employé</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Montant total:</span>
                          <span className={`ml-2 font-bold text-lg ${penaliteInfo.type === "retard" ? "text-orange-800" : "text-red-800"}`}>
                            {penaliteInfo.montant?.toLocaleString()} GNF
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Upload preuve pénalité */}
                    <div className="mt-4 border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white">
                      <h4 className="font-semibold mb-2">Preuve de Paiement de la Pénalité</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Veuillez uploader la preuve de paiement de la pénalité ({penaliteInfo.montant?.toLocaleString()} GNF) au compte de l'IGT.
                      </p>
                      
                      {preuvePenaliteFilename ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">{preuvePenaliteFilename}</span>
                            <CheckCircle className="w-5 h-5" />
                          </div>
                        </div>
                      ) : preuvePenalite ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-2 text-blue-700">
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">{preuvePenalite.name}</span>
                          </div>
                        </div>
                      ) : null}
                      
                      <input
                        type="file"
                        id="preuve-penalite"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error("Le fichier est trop volumineux (max 10MB)");
                              return;
                            }
                            setPreuvePenalite(file);
                            if (id) {
                              handleUploadPreuvePenalite(file, id);
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById("preuve-penalite").click()}
                        disabled={uploadingPenalite}
                      >
                        {uploadingPenalite ? "Upload en cours..." : preuvePenaliteFilename ? "Remplacer" : "Sélectionner un fichier"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section Paiement 10% */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Paiement Obligatoire des 10%</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Avant de soumettre votre bilan social, vous devez effectuer le paiement de <strong>10% de la facture</strong> de l'entreprise au compte de l'IGT (Inspection Générale du Travail).
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    Ce paiement s'effectue <strong>hors plateforme</strong>. Une fois le paiement effectué, veuillez importer la preuve de paiement ci-dessous.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6">
              <div className="text-center">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Preuve de Paiement des 10%</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Formats acceptés: PDF, JPG, PNG (max 10MB)
                </p>
                
                {preuvePaiementFilename ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center gap-2 text-emerald-700">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">{preuvePaiementFilename}</span>
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">Document uploadé avec succès</p>
                  </div>
                ) : preuvePaiement ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center gap-2 text-blue-700">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">{preuvePaiement.name}</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">Document sélectionné (sera uploadé lors de la soumission)</p>
                  </div>
                ) : null}
                
                <input
                  type="file"
                  id="preuve-paiement"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error("Le fichier est trop volumineux (max 10MB)");
                        return;
                      }
                      setPreuvePaiement(file);
                      if (id) {
                        handleUploadPreuve(file, id);
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("preuve-paiement").click()}
                  disabled={uploadingPreuve}
                >
                  {uploadingPreuve ? (
                    "Upload en cours..."
                  ) : preuvePaiementFilename ? (
                    "Remplacer le document"
                  ) : (
                    "Sélectionner un fichier"
                  )}
                </Button>
              </div>
            </div>

            {/* Résumé de la soumission */}
            {paiement10Ready && penaliteReady && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Prêt pour la soumission</span>
                </div>
                <p className="text-sm text-emerald-600 mt-1">
                  Tous les documents requis sont enregistrés. Vous pouvez maintenant soumettre votre bilan social.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pb-32" data-testid="bilan-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/bilans">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {id ? "Modifier le Bilan" : "Nouveau Bilan Social"}
          </h1>
          <p className="text-muted-foreground mt-1">
            Remplissez toutes les sections du formulaire
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        {STEPS.map((s, index) => (
          <div key={s.id} className="flex items-center">
            <button
              onClick={() => setStep(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                step === s.id
                  ? "bg-primary text-white"
                  : step > s.id
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-white/20">
                {step > s.id ? <CheckCircle className="w-4 h-4" /> : s.id}
              </span>
              <span className="hidden md:inline text-sm font-medium">{s.title}</span>
            </button>
            {index < STEPS.length - 1 && (
              <div className="w-8 h-0.5 bg-slate-200 mx-2" />
            )}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">{STEPS[step - 1].title}</CardTitle>
          <CardDescription>{STEPS[step - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>{renderStepContent()}</CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          data-testid="form-prev-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Précédent
        </Button>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="form-save-btn">
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>

          {step === STEPS.length ? (
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={handleSubmitClick}
              disabled={
                saving || 
                !formData.enterprise_id || 
                (!preuvePaiement && !preuvePaiementFilename) ||
                (penaliteInfo && penaliteInfo.type !== "aucune" && !preuvePenaliteFilename && !preuvePenalite)
              }
              data-testid="submit-bilan-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {saving ? "Envoi..." : "Soumettre le Bilan"}
            </Button>
          ) : (
            <Button onClick={() => setStep(Math.min(STEPS.length, step + 1))} data-testid="form-next-btn">
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Warning Dialog for Bilan Submission */}
      <ResponsabilityWarningDialog
        open={showWarningDialog}
        onOpenChange={setShowWarningDialog}
        onConfirm={handleConfirmedSubmit}
        type="bilan"
        loading={saving}
      />
    </div>
  );
}
